import { useCallback, useEffect, useRef, useState } from 'react'
import { ContextualHelp, PageTitle, TaskItem } from '@components'
import { PRIORITY } from '@constants'
import { useTask } from '@hooks'
import './eisenhower-matrix.style.scss'

const MATRIX_HELP_SUMMARY =
  'A matriz ajuda a decidir o que fazer primeiro. Ela cruza duas perguntas simples: isso \u00e9 urgente? Isso \u00e9 importante?'

const MATRIX_HELP_ITEMS = [
  {
    title: 'Urgente e importante',
    description: 'Resolva primeiro. \u00c9 prazo, problema ou algo que n\u00e3o pode esperar.',
  },
  {
    title: 'N\u00e3o urgente e importante',
    description: 'Planeje com carinho. Normalmente \u00e9 o que mais faz voc\u00ea avan\u00e7ar.',
  },
  {
    title: 'Urgente e n\u00e3o importante',
    description: 'Fa\u00e7a r\u00e1pido ou delegue, se der. N\u00e3o deixe roubar seu dia.',
  },
  {
    title: 'N\u00e3o urgente e n\u00e3o importante',
    description: 'Deixe para depois ou repense se precisa mesmo ficar na lista.',
  },
]

const MATRIX_HELP_TIPS = [
  'Arraste as tarefas entre os quadrantes quando a prioridade mudar.',
]

const CONFIG_MATRIX = {
  URGENTE_IMPORTANTE: { class: 'urgente-importante', color: '#f29166' },
  NAO_URGENTE_IMPORTANTE: {
    class: 'nao-urgente-importante',
    color: '#f3ca40',
  },
  URGENTE_NAO_IMPORTANTE: {
    class: 'urgente-nao-importante',
    color: '#2e7f7b',
  },
  NAO_URGENTE_NAO_IMPORTANTE: {
    class: 'nao-urgente-nao-importante',
    color: '#788bf5',
  },
}

const EisenhowerMatrixScreen = () => {
  const [taskList, setTaskList] = useState({
    URGENTE_IMPORTANTE: [],
    NAO_URGENTE_IMPORTANTE: [],
    URGENTE_NAO_IMPORTANTE: [],
    NAO_URGENTE_NAO_IMPORTANTE: [],
  })
  const [dragOverQuadrant, setDragOverQuadrant] = useState(null)
  const draggedTaskRef = useRef(null)

  const { getTasksMatrix, updateTask, deleteTask } = useTask()

  const getTaskList = useCallback(() => {
    const resultado = getTasksMatrix()

    if (resultado) {
      setTaskList(resultado)
    }
  }, [getTasksMatrix])

  useEffect(() => {
    getTaskList()
  }, [getTaskList])

  const handleChangeTask = ({ task, check }) => {
    const data = check ? { ...task, estaRealizado: !task.estaRealizado } : task

    updateTask({ data, id: task.id })
    getTaskList()
  }

  const handleChangeDeleteTask = ({ id }) => {
    deleteTask(id)
    getTaskList()
  }

  const handleClickSaveAlter = task =>
    updateTask({ data: task, id: task.id })

  const handleDragStart = task => event => {
    draggedTaskRef.current = task
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/plain', task.id)
  }

  const handleDragEnd = () => {
    draggedTaskRef.current = null
    setDragOverQuadrant(null)
  }

  const handleDragOverQuadrant = priority => event => {
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDragOverQuadrant(priority)
  }

  const handleDropOnQuadrant = priority => event => {
    event.preventDefault()

    const draggedTask = draggedTaskRef.current
    setDragOverQuadrant(null)

    if (!draggedTask || draggedTask.prioridade === priority) {
      draggedTaskRef.current = null
      return
    }

    updateTask({
      id: draggedTask.id,
      data: {
        ...draggedTask,
        prioridade: priority,
      },
    })

    draggedTaskRef.current = null
    getTaskList()
  }

  const renderTaskItem = ({ value, color, className }) => {
    if (!taskList[value]) {
      return []
    }

    return taskList[value].map(task => (
      <TaskItem
        key={task.id}
        task={task}
        handleChangeTask={handleChangeTask}
        color={color}
        className={className}
        handleClickSaveAlter={handleClickSaveAlter}
        handleClickDelete={handleChangeDeleteTask}
        dragHandleProps={{
          draggable: true,
          onDragStart: handleDragStart(task),
          onDragEnd: handleDragEnd,
          title: 'Arrastar para outro quadrante',
        }}
      />
    ))
  }

  const renderTaskList = () => {
    if (!taskList) {
      return null
    }

    return PRIORITY.map(({ name, value }, index) => (
      <div
        className={`eisenhower-matrix__container eisenhower-matrix__container__${CONFIG_MATRIX[value].class} ${
          dragOverQuadrant === value
            ? 'eisenhower-matrix__container--drag-over'
            : ''
        }`}
        key={index}
        onDragOver={handleDragOverQuadrant(value)}
        onDragLeave={() =>
          setDragOverQuadrant(current => (current === value ? null : current))
        }
        onDrop={handleDropOnQuadrant(value)}
      >
        <div
          className={`eisenhower-matrix__titulo eisenhower-matrix__titulo__${CONFIG_MATRIX[value].class}`}
        >
          {name}
        </div>

        <ul className="eisenhower-matrix__list">
          {renderTaskItem({
            value,
            color: CONFIG_MATRIX[value].color,
            className: CONFIG_MATRIX[value].class,
          })}
        </ul>
      </div>
    ))
  }

  return (
    <section className="eisenhower-matrix">
      <div className="eisenhower-matrix__header">
        <PageTitle
          titulo="Matriz de Eisenhower"
          className="eisenhower-matrix__titulo-pagina"
        />
        <ContextualHelp
          className="eisenhower-matrix__help"
          title="Como usar a Matriz de Eisenhower"
          summary={MATRIX_HELP_SUMMARY}
          items={MATRIX_HELP_ITEMS}
          tips={MATRIX_HELP_TIPS}
        />
      </div>

      <div className="eisenhower-matrix__conteudo">{renderTaskList()}</div>
    </section>
  )
}

export { EisenhowerMatrixScreen }
